import JsBarcode from "jsbarcode";
import type { NodeBarcode } from "./types";

const DEFAULT_EAN = "5901234123457";

export function ean13DigitsOnly(raw: string): string {
  return raw.replace(/\D/g, "");
}

/** 12 digits for JsBarcode EAN13 (check digit optional / recalculated). */
export function ean13Payload(raw: string): string {
  const d = ean13DigitsOnly(raw);
  if (d.length === 0) return DEFAULT_EAN;
  if (d.length >= 12) return d.slice(0, 13);
  return (d + "000000000000").slice(0, 12);
}

function bgForJsBarcode(bg: string): string {
  if (bg === "transparent") return "rgba(255,255,255,0)";
  return bg;
}

export function paintBarcodeOnSvgElement(
  svg: SVGSVGElement,
  node: Pick<NodeBarcode, "format" | "value" | "lineColor" | "background" | "h">,
): void {
  const format = node.format;
  const payload =
    format === "EAN13" ? ean13Payload(node.value) : node.value.length > 0 ? node.value : " ";
  const h = Math.max(10, Math.round(node.h));
  const fontSize = Math.max(10, Math.round(h * 0.14));
  const barHeight = Math.max(24, Math.round(h * 0.58));

  const opts = {
    format,
    displayValue: true,
    fontSize,
    textMargin: 4,
    margin: 2,
    lineColor: node.lineColor,
    background: bgForJsBarcode(node.background),
    width: 2,
    height: barHeight,
  };

  const run = (v: string) => {
    JsBarcode(svg, v, opts);
  };

  try {
    run(payload);
  } catch {
    while (svg.firstChild) svg.removeChild(svg.firstChild);
    try {
      run(format === "EAN13" ? DEFAULT_EAN : " ");
    } catch {
      while (svg.firstChild) svg.removeChild(svg.firstChild);
      JsBarcode(svg, format === "EAN13" ? DEFAULT_EAN : " ", {
        ...opts,
        format: format === "EAN13" ? ("EAN13" as const) : ("CODE128" as const),
      });
    }
  }
}

export function makeBarcodeDataUrl(node: Pick<NodeBarcode, "format" | "value" | "lineColor" | "background" | "h">): string {
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  paintBarcodeOnSvgElement(svg, node);
  const raw = new XMLSerializer().serializeToString(svg);
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(raw)}`;
}

export function barcodeSvgInnerForExport(node: NodeBarcode): string {
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  paintBarcodeOnSvgElement(svg, node);
  const raw = new XMLSerializer().serializeToString(svg);
  return raw.replace(
    /<svg[^>]*>/,
    `<svg xmlns="http://www.w3.org/2000/svg" width="${node.w}" height="${node.h}" viewBox="0 0 ${node.w} ${node.h}">`,
  );
}
